/* global process, fetch, RequestInit, HeadersInit */

import { getAppApiName } from "@/services/platform";

const API_URL = `${process.env.WEB_APP_URL!}/api/v1`;

interface RequestConfig extends RequestInit {
  requiresAuth?: boolean;
  organizationId?: string;
}

export class APIError extends Error {
  constructor(
    message: string,
    public status: number,
    public response: any
  ) {
    super(message);
    this.name = "APIError";
  }
}

class APIClient {
  private baseURL: string;
  private tokenProvider: (() => Promise<string>) | null = null;

  constructor(baseURL: string) {
    this.baseURL = baseURL;
  }

  setTokenProvider(provider: () => Promise<string>) {
    this.tokenProvider = provider;
  }

  private async getHeaders(config: RequestConfig): Promise<HeadersInit> {
    const headers: HeadersInit = {
      "Content-Type": "application/json",
    };

    if (config.requiresAuth !== false && this.tokenProvider) {
      const token = await this.tokenProvider();
      headers["Authorization"] = `Bearer ${token}`;
    }

    if (config.organizationId) {
      headers["x-org-id"] = config.organizationId;
    }

    return headers;
  }

  async request<T>(endpoint: string, config: RequestConfig = {}): Promise<T> {
    const appName = getAppApiName();
    const isQueryEndpoint = endpoint.startsWith("/query");
    const url = isQueryEndpoint
      ? `${this.baseURL}/${appName}${endpoint}`
      : `${this.baseURL}/office/${appName}${endpoint}`;

    const headers = await this.getHeaders(config);

    const response = await fetch(url, {
      ...config,
      headers: { ...headers, ...config.headers },
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new APIError(error.message || error.error || response.statusText, response.status, error);
    }

    return response.json();
  }

  get<T>(endpoint: string, config?: RequestConfig): Promise<T> {
    return this.request<T>(endpoint, { ...config, method: "GET" });
  }

  post<T>(endpoint: string, data?: any, config?: RequestConfig): Promise<T> {
    return this.request<T>(endpoint, {
      ...config,
      method: "POST",
      body: data ? JSON.stringify(data) : undefined,
    });
  }

  delete<T>(endpoint: string, data?: any, config?: RequestConfig): Promise<T> {
    return this.request<T>(endpoint, {
      ...config,
      method: "DELETE",
      body: data ? JSON.stringify(data) : undefined,
    });
  }

  patch<T>(endpoint: string, data?: any, config?: RequestConfig): Promise<T> {
    return this.request<T>(endpoint, {
      ...config,
      method: "PATCH",
      body: data ? JSON.stringify(data) : undefined,
    });
  }

  put<T>(endpoint: string, data?: any, config?: RequestConfig): Promise<T> {
    return this.request<T>(endpoint, {
      ...config,
      method: "PUT",
      body: data ? JSON.stringify(data) : undefined,
    });
  }
}

export const apiClient = new APIClient(API_URL);
